/**
 * Particle system types and configuration.
 */

import * as THREE from 'three/webgpu';
import { defineComponent, defineTag } from '../ecs/Component.js';
import { ComponentType } from '../shared/types.js';

// ─── Particle Emitter Shape ─────────────────────────────────────────────────

export type EmitterShape =
  | { type: 'point' }
  | { type: 'sphere'; radius: number }
  | { type: 'box'; halfExtents: THREE.Vector3 }
  | { type: 'cone'; angle: number; radius: number; height: number }
  | { type: 'circle'; radius: number }
  | { type: 'hemisphere'; radius: number };

// ─── Over-Lifetime Curve ────────────────────────────────────────────────────

export interface CurveKeyframe {
  time: number; // 0..1 (normalized lifetime)
  value: number;
}

export interface GradientKeyframe {
  time: number;
  color: THREE.Color;
}

// ─── Particle Module Configs ────────────────────────────────────────────────

export interface EmissionConfig {
  rateOverTime: number;       // Particles per second (continuous)
  bursts?: BurstConfig[];     // Scheduled bursts
}

export interface BurstConfig {
  time: number;               // Seconds from emitter start
  count: number;              // Number of particles
  cycles?: number;            // Repeat count (0 = infinite)
  interval?: number;          // Seconds between cycles
}

export interface ParticleConfig {
  /** Maximum particles alive at once */
  maxParticles: number;
  /** Particle lifetime in seconds */
  lifetime: number | { min: number; max: number };
  /** Starting speed */
  startSpeed: number | { min: number; max: number };
  /** Starting size */
  startSize: number | { min: number; max: number };
  /** Starting color */
  startColor: THREE.ColorRepresentation;
  /** Starting rotation (radians) */
  startRotation?: number | { min: number; max: number };
  /** Gravity multiplier */
  gravityModifier?: number;
  /** Simulation space */
  simulationSpace?: 'local' | 'world';
  /** Emitter shape */
  shape?: EmitterShape;
  /** Emission config */
  emission?: EmissionConfig;
  /** Size over lifetime curve */
  sizeOverLifetime?: CurveKeyframe[];
  /** Color over lifetime gradient */
  colorOverLifetime?: GradientKeyframe[];
  /**
   * Alpha over lifetime curve. Values in [0, 1]. When omitted and no
   * `colorOverLifetime` is supplied, particles get a gentle linear fade
   * in their last 20% of life so they don't pop out of existence.
   */
  alphaOverLifetime?: CurveKeyframe[];
  /** Speed over lifetime curve */
  speedOverLifetime?: CurveKeyframe[];
  /** Rotation speed (radians/sec) */
  rotationOverLifetime?: number;
  /** Noise force */
  noise?: { strength: number; frequency: number; scrollSpeed: number };
  /**
   * Rendering mode. 'stretched' orients each quad along the particle's
   * VELOCITY on screen instead of the camera's up axis — what streak sprites
   * (rain, driven sand) need, since a plain billboard pins the sprite's +Y to
   * SCREEN up and the streaks swing round as soon as the camera tilts.
   * Implemented on the GPU path (GpuParticleMaterial); 'mesh' is not.
   */
  renderMode?: 'billboard' | 'stretched' | 'mesh';
  /**
   * Six-way lit smoke (2026-09-15): two baked directional-response flipbooks
   * (see gpu/SixWaySmoke.ts). WebGPU only — the CPU adapter ignores it and
   * draws its plain sprite.
   */
  sixWay?: { mapA: THREE.Texture; mapB: THREE.Texture; columns: number; rows: number; fps?: number } | null;
  /** How far to stretch along the velocity in 'stretched' mode (default 2.5×). */
  velocityStretch?: number;
  /** Texture */
  texture?: THREE.Texture;
  /** Blending mode */
  blending?: THREE.Blending;
  /** Depth write */
  depthWrite?: boolean;
  /** Sort particles back-to-front */
  sortParticles?: boolean;
}

// ─── Particle GPU Data Layout (SoA in storage buffers) ──────────────────────

export const PARTICLE_STRIDE = 16; // floats per particle
// Layout: [px, py, pz, vx, vy, vz, age, lifetime, size, r, g, b, a, rotation, startSize, startSpeed]

// ─── ECS Components ─────────────────────────────────────────────────────────

export const ParticleEmitterComponent = defineComponent('ParticleEmitter', {
  maxParticles: ComponentType.U32,
  activeCount: ComponentType.U32,
  emissionRate: ComponentType.F32,
  elapsed: ComponentType.F32,
  playing: ComponentType.Boolean,
});

export const ParticleEmitterTag = defineTag('HasParticleEmitter');

// ─── Default Config ─────────────────────────────────────────────────────────

export const DEFAULT_PARTICLE_CONFIG: ParticleConfig = {
  maxParticles: 1000,
  lifetime: 2,
  startSpeed: 5,
  startSize: 0.1,
  startColor: 0xffffff,
  gravityModifier: 0,
  simulationSpace: 'world',
  shape: { type: 'cone', angle: Math.PI / 6, radius: 0.1, height: 1 },
  emission: { rateOverTime: 100 },
  renderMode: 'billboard',
  blending: THREE.AdditiveBlending,
  depthWrite: false,
  sortParticles: false,
};
